/**
 * FormPanel — bordered card that wraps a form section.
 *
 *   <FormPanel meta={<><b>4</b>/6 vyplněno</>}>
 *     <SectionHeader number={3} title={<>Kdo je <em>klient</em>?</>} />
 *     <Fields cols={2}>…</Fields>
 *   </FormPanel>
 *
 * The header bar carries an optional left `eyebrow` and right `meta`. When a
 * panel wraps a numbered `SectionHeader`, skip the `eyebrow` — the number +
 * title below already name the section, and repeating it in the bar is just
 * noise. Reserve `eyebrow` for label-only panels (summary/aside cards) that
 * have no SectionHeader of their own.
 *
 * Source: .agents/design/project/artboard-radical-zakaznik-form.jsx:117-161
 */

import type { ReactNode } from 'react';

interface FormPanelProps {
  /** Mono uppercase tag on the left of the header. Omit when the panel has a SectionHeader. */
  eyebrow?: ReactNode;
  /** Right-side header meta (e.g. progress). `<b>` becomes ok green per the design. */
  meta?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function FormPanel({ eyebrow, meta, children, className = '' }: FormPanelProps) {
  const showHeader = !!(eyebrow || meta);
  return (
    <section className={'border border-line bg-paper ' + className}>
      {showHeader && (
        <header className="flex items-baseline justify-between gap-4 border-b border-line px-6 py-2.5 max-sm:px-4">
          {eyebrow ? (
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              {eyebrow}
            </span>
          ) : (
            <span />
          )}
          {meta && (
            <span className="font-mono text-[11.5px] text-ink-3 [&_b]:font-medium [&_b]:text-ok">
              {meta}
            </span>
          )}
        </header>
      )}
      <div className="px-7 pb-2 pt-5 max-sm:px-4">{children}</div>
    </section>
  );
}

interface SectionHeaderProps {
  /** Number rendered inside the bordered square. Provide as string (e.g. "01") or number. */
  number: number | string;
  /** Display title (Fraunces serif). Wrap italics in `<em>`. */
  title: ReactNode;
  /** Optional sub-paragraph beneath the title, indented to align under it. */
  description?: ReactNode;
  className?: string;
}

export function SectionHeader({
  number,
  title,
  description,
  className = '',
}: SectionHeaderProps) {
  const numStr = typeof number === 'number' ? String(number).padStart(2, '0') : number;
  return (
    <header className={'mb-4 ' + className}>
      {/* Phone (14.7): handoff PhoneEditVozidlo scales the number square to
          ~34px and the title to ~19px so section heads fit a 358px panel. */}
      <div className="flex items-center gap-4">
        <div className="grid size-11 shrink-0 place-items-center border border-line-2 font-mono max-sm:size-9">
          <span className="text-[17px] font-medium tracking-[-0.02em] text-ink max-sm:text-[14px]">{numStr}</span>
        </div>
        <h2 className="font-display text-[28px] font-normal leading-[1.1] tracking-[-0.02em] text-ink [&>em]:font-light [&>em]:italic [&>em]:text-ink-3 max-sm:text-[20px]">
          {title}
        </h2>
      </div>
      {description && (
        <p className="mt-2 max-w-[560px] pl-[60px] text-[13px] leading-[1.5] text-ink-3 max-sm:pl-[52px]">
          {description}
        </p>
      )}
    </header>
  );
}
